import React from 'react'
import { CSVLink } from "react-csv";
import { normalizePhone } from '../../utils/functions/tools/normalizePhone';
import {
    DataTable,
    Table,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableSelectAll,
    TableSelectRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Modal,
    NotificationActionButton,
    InlineNotification,
    OverflowMenu,
    OverflowMenuItem,
    Form,
    TextInput
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Save16 as Save,
    Download16 as Download,
    Edit16 as Edit
} from '@carbon/icons-react';

const countriesHeaders = [
    { header: "ID", key: "id" },
    { header: "Description", key: "country" },
    { header: "Code", key: "countrycode" },
    { header: "Action", key: "action" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const CountriesList = ({ setDeleteID, csvCountryReport, setCountrySearch, deleteID, batchDelete, openDeleteModal, setOpenDeleteModal, editID, editSetup, editForm, openEditModal, setOpenEditModal, editCountry, addCountry, addForm, openAddModal, setOpenAddModal, error, setError, rows, totalItems, currentPageSize, setCurrentPageSize, setFirstRowIndex }: any) => (
    <>
        <DataTable rows={rows} headers={countriesHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getSelectionProps,
                getToolbarProps,
                getBatchActionProps,
                selectedRows,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableBatchActions {...batchActionProps}>
                                <TableBatchAction
                                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                                    renderIcon={Delete}
                                    onClick={() => setOpenDeleteModal(true) & setDeleteID(selectedRows)}
                                >
                                    Delete
                                </TableBatchAction>
                            </TableBatchActions>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <TableToolbarSearch
                                    persistent={true}
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    onChange={(e) => setCountrySearch(e.target.value)}
                                    placeholder="Search countries"
                                />
                                <TableToolbarMenu
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}>
                                    <TableToolbarAction>
                                        <CSVLink className="text-dark dec-none" {...csvCountryReport}>Export to CSV</CSVLink>
                                    </TableToolbarAction>
                                </TableToolbarMenu>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddModal(true)}
                                >
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    <TableSelectAll {...getSelectionProps()} />
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        <TableSelectRow {...getSelectionProps({ row })} />
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                    <OverflowMenuItem onClick={()=>editSetup(row.id) & setOpenEditModal(true)} itemText="Edit" />
                                                    <OverflowMenuItem onClick={()=>setDeleteID([row]) & setOpenDeleteModal(true)} hasDivider isDelete itemText="Delete" />
                                                </OverflowMenu> : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentPageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentPageSize) {
                                    setCurrentPageSize(pageSize);
                                }
                                setFirstRowIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddModal}
            modalHeading="Add country"
            modalLabel="Countries Reference Data"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddModal(false) & setError(false)}
            onRequestSubmit={() => addForm.current && addForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add new country" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new country that can be used within the system.
            </p>
            <Form id="addCountry" onSubmit={(values) => addCountry(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="country"
                        name="country"
                        labelText="Country Description"
                        placeholder="Enter description"
                        invalidText="Country Description is required"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="countrycode"
                        name="countrycode"
                        labelText="Country Code"
                        placeholder="Enter country code"
                        invalidText="Country Code is required"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditModal}
            modalHeading="Edit country"
            modalLabel="Countries Reference Data"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditModal(false) & setError(false)}
            onRequestSubmit={() => editForm.current && editForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to edit country" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected country.
            </p>
            <Form id="addCurrency" onSubmit={(values) => editCountry(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="country"
                        name="country"
                        labelText="Country Description"
                        placeholder="Enter description"
                        invalidText="Country Description is required"
                        style={{ marginBottom: '1rem' }}
                        defaultValue={editID?.country}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="countrycode"
                        name="countrycode"
                        labelText="Country Code"
                        placeholder="Enter country code"
                        invalidText="Country Code is required"
                        style={{ marginBottom: '1rem' }}
                        defaultValue={editID?.countrycode}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeleteModal}
            modalHeading="Delete Country"
            modalLabel="Countries Reference Data"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeleteModal(false) & setError(false)}
            onRequestSubmit={() => batchDelete(deleteID)}
        >
            {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete country" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected countries?</p>

        </Modal>
    </>
);